import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Loader2, Package, Edit3, Save, Sparkles, Upload } from 'lucide-react';
import { adminApi } from '../lib/api';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const emptyProduct = { name: '', category: 'Sécurité', price: 0, description: '', image: '', stock: 10, featured: false, coming_soon: false };

const ProductForm = ({ initial, onCancel, onSave }) => {
  const [form, setForm] = useState({
    ...emptyProduct,
    ...initial,
    // Admin form manipulates price HT (DB stores HT)
    price: initial ? (initial.price_ht ?? initial.price) : 0,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial?.id;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image trop lourde (max 2 Mo)');
      return;
    }
    setUploading(true);
    // Compress via canvas to max 800px
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 800;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setForm((f) => ({ ...f, image: dataUrl }));
        setUploading(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const payload = { ...form, price: parseInt(form.price) || 0, stock: parseInt(form.stock) || 0 };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 border-2 border-[#F4A340]/30 space-y-3">
      <h3 className="font-bold text-slate-900">{isEdit ? 'Modifier le produit' : 'Nouveau produit'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
        <input placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
        <div>
          <input type="number" placeholder="Prix HT F CFA" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          {form.price > 0 && (
            <p className="text-[10px] text-slate-500 mt-1">
              = <span className="font-bold text-[#E89534]">{new Intl.NumberFormat('fr-FR').format(Math.round((parseInt(form.price) || 0) * 1.18))} F TTC</span> (TVA 18%)
            </p>
          )}
        </div>
        <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
      </div>

      {/* Image uploader */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Photo du produit</label>
        <div className="flex gap-3 items-start">
          <div className="w-28 h-28 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
            {form.image ? (
              <img src={form.image} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <Package className="w-8 h-8 text-slate-400" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-semibold w-full justify-center">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</> : <><Upload className="w-4 h-4" /> Téléverser une image</>}
            </button>
            <input placeholder="Ou coller une URL d'image" value={form.image?.startsWith('data:') ? '' : (form.image || '')} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full px-4 py-2 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm" />
            {form.image && (
              <button type="button" onClick={() => setForm({ ...form, image: '' })} className="text-xs text-red-600 hover:underline">Retirer l'image</button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">Max 2 Mo · Redimensionnée automatiquement à 800 px</p>
      </div>

      <textarea placeholder="Description" rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none resize-none" />

      <div className="flex flex-wrap gap-4 pt-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#F4A340]" />
          Produit populaire
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.coming_soon} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} className="accent-[#F4A340]" />
          Marquer "À venir"
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#F4A340]" />
          Actif (visible sur le site)
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
        <button onClick={submit} disabled={saving || !form.name} className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
      </div>
    </div>
  );
};

const ShopManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.getProducts()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (payload) => {
    await adminApi.createProduct(payload);
    setShowNew(false);
    load();
  };

  const handleUpdate = async (payload) => {
    await adminApi.updateProduct(editing.id, payload);
    setEditing(null);
    load();
  };

  const remove = async (p) => {
    if (window.confirm(`Supprimer "${p.name}" ?`)) {
      await adminApi.deleteProduct(p.id);
      load();
    }
  };

  const stats = {
    total: items.length,
    active: items.filter(p => p.active && !p.coming_soon).length,
    comingSoon: items.filter(p => p.coming_soon).length,
    lowStock: items.filter(p => !p.coming_soon && p.stock < 5).length,
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Boutique · Produits</h3>
          <p className="text-sm text-slate-500">
            {stats.total} produits · {stats.active} actifs · {stats.comingSoon} à venir · {stats.lowStock} en stock faible
          </p>
        </div>
        <button onClick={() => { setShowNew(true); setEditing(null); }} className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">
          <Plus className="w-4 h-4" /> Nouveau produit
        </button>
      </div>

      {showNew && (
        <ProductForm onCancel={() => setShowNew(false)} onSave={handleCreate} />
      )}
      {editing && (
        <ProductForm initial={editing} onCancel={() => setEditing(null)} onSave={handleUpdate} />
      )}

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className={`bg-white rounded-2xl border overflow-hidden ${p.coming_soon ? 'border-slate-200 opacity-90' : 'border-slate-200'}`}>
              <div className="relative aspect-video bg-slate-50">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                    <Package className="w-10 h-10" strokeWidth={1} />
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  {p.featured && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4A340] text-white font-semibold flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" /> Populaire
                    </span>
                  )}
                  {p.coming_soon && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900 text-white font-semibold">À venir</span>
                  )}
                  {!p.active && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Inactif</span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{p.category}</div>
                <div className="font-bold text-slate-900 mt-0.5">{p.name}</div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-xl font-bold text-slate-900">{formatFCFA(p.price_ht ?? p.price)}</span>
                  <span className="text-xs text-slate-500">F HT · <b>{formatFCFA(p.price)} TTC</b></span>
                  <span className="ml-auto text-xs text-slate-500">Stock: <b>{p.stock}</b></span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => { setEditing(p); setShowNew(false); }} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors">
                    <Edit3 className="w-3.5 h-3.5" /> Éditer
                  </button>
                  <button onClick={() => remove(p)} className="px-3 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopManagement;
